import { useState } from 'react';
import toast from 'react-hot-toast';
import NicknameBar from '../components/NicknameBar';
import { useCountdown } from '../hooks/useCountdown';

interface LotteryTexts {
  votingQuestion?: string;
  buttonYes?: string;
  buttonNo?: string;
  winnerTitle?: string;
  winnerSubtitle?: string;
}

interface LotteryVotingScreenProps {
  lotteryId: string;
  endsAt: number;
  serverTime: number;
  texts?: LotteryTexts;
  initialHasVoted?: boolean;
  initialVotedYes?: boolean;
}

function LotteryVotingScreen({ lotteryId, endsAt, serverTime, texts, initialHasVoted, initialVotedYes }: LotteryVotingScreenProps) {
  const remainingSeconds = useCountdown(endsAt, serverTime);
  const [hasVoted, setHasVoted] = useState(initialHasVoted || false);
  const [votedYes, setVotedYes] = useState<boolean | null>(initialVotedYes ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleVote = async (wantsToParticipate: boolean) => {
    if (hasVoted || isSubmitting) return;

    const audienceId = localStorage.getItem('audienceId');
    if (!audienceId) {
      toast.error('Sessione non valida');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/lottery/${lotteryId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audienceId, wantsToParticipate }),
      });

      if (response.ok) {
        setHasVoted(true);
        setVotedYes(wantsToParticipate);
        toast.success(wantsToParticipate ? 'Partecipi alla lotteria!' : 'Risposta registrata');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore durante il voto');
      }
    } catch (error) {
      console.error('[LotteryVotingScreen] Error submitting vote:', error);
      toast.error('Errore di connessione');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen p-4 pt-10">
      <NicknameBar />

      {/* Timer */}
      <div className="text-center py-4">
        <div className={`text-5xl font-bold ${remainingSeconds <= 3 ? 'text-team-red animate-pulse' : 'text-ink'}`}>
          {remainingSeconds}
        </div>
      </div>

      {/* Domanda */}
      <div className="text-center py-6">
        <h2 className="text-2xl font-heading font-bold text-ink mb-2">
          {texts?.votingQuestion || 'Vuoi poter essere coinvolto?'}
        </h2>
      </div>

      {/* Pulsanti */}
      {!hasVoted ? (
        <div className="flex-1 flex flex-col justify-center space-y-4 max-w-md mx-auto w-full px-4">
          <button
            onClick={() => handleVote(true)}
            disabled={isSubmitting}
            className="w-full py-6 px-6 rounded-xl text-2xl font-bold transition-all bg-team-green hover:bg-team-green/80 active:scale-95 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {texts?.buttonYes || 'Si si SIII!'}
          </button>

          <button
            onClick={() => handleVote(false)}
            disabled={isSubmitting}
            className="w-full py-6 px-6 rounded-xl text-2xl font-bold transition-all bg-team-red hover:bg-team-red/80 active:scale-95 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {texts?.buttonNo || 'NO, lasciatemi in pace!'}
          </button>
        </div>
      ) : (
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className={`text-8xl mb-4 ${votedYes ? 'animate-bounce' : ''}`}>
            {votedYes ? '🎲' : '😌'}
          </div>
          <p className={`text-2xl font-bold ${votedYes ? 'text-team-green' : 'text-ink-light'}`}>
            {votedYes ? 'Partecipi!' : 'Ok, tranquillo!'}
          </p>
          {votedYes && (
            <p className="text-ink-light mt-2">In attesa dell'estrazione...</p>
          )}
        </div>
      )}
    </div>
  );
}

export default LotteryVotingScreen;
